import * as React from "react";
import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { getMyAdminStatus } from "@/lib/billing.functions";
import {
  listAllNamedReferrals,
  listPartners,
  setPartnerStatus,
  type PartnerRow,
} from "@/lib/partners.functions";

export const Route = createFileRoute("/_authed/admin/partners")({
  head: () => ({
    meta: [
      { title: "Admin partners: CapitalDart" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  beforeLoad: async () => {
    if (typeof window === "undefined") return;
    const status = await getMyAdminStatus();
    if (!status.isAdmin) throw redirect({ to: "/dashboard" });
  },
  component: AdminPartnersPage,
});

function money(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function AdminPartnersPage() {
  const qc = useQueryClient();
  const listFn = useServerFn(listPartners);
  const refsFn = useServerFn(listAllNamedReferrals);
  const setStatusFn = useServerFn(setPartnerStatus);

  const partners = useQuery({ queryKey: ["admin-partners"], queryFn: () => listFn() });
  const referrals = useQuery({
    queryKey: ["admin-partner-referrals"],
    queryFn: () => refsFn(),
  });

  return (
    <main className="mx-auto max-w-[1080px] px-4 pb-16 pt-6">
      <Link
        to="/admin"
        className="text-[12.5px] text-brand-muted no-underline hover:text-brand-ink"
      >
        ← Back to admin
      </Link>
      <h1 className="mt-2 font-serif text-[32px] font-normal leading-tight tracking-tight text-brand-ink">
        Partners.
      </h1>
      <p className="mt-1 max-w-[70ch] text-[14px] text-brand-muted">
        Applications, approvals and named referrals. Approving a partner sets
        their link slug, which is also the slug used on the payment platform.
      </p>

      <section className="mt-6">
        <h2 className="mb-3 font-serif text-[20px] text-brand-ink">Applications</h2>
        {partners.isLoading ? (
          <p className="text-[14px] text-brand-muted">Loading…</p>
        ) : !partners.data || partners.data.length === 0 ? (
          <p className="text-[14px] text-brand-muted">No applications yet.</p>
        ) : (
          <div className="space-y-3">
            {partners.data.map((p) => (
              <PartnerCard
                key={p.id}
                row={p}
                onSave={async (patch) => {
                  await setStatusFn({ data: { id: p.id, ...patch } });
                  qc.invalidateQueries({ queryKey: ["admin-partners"] });
                }}
              />
            ))}
          </div>
        )}
      </section>

      <section className="mt-10">
        <h2 className="mb-3 font-serif text-[20px] text-brand-ink">Named referrals</h2>
        {referrals.isLoading ? (
          <p className="text-[14px] text-brand-muted">Loading…</p>
        ) : !referrals.data || referrals.data.length === 0 ? (
          <p className="text-[14px] text-brand-muted">None submitted yet.</p>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-brand-line bg-brand-paper">
            <table className="w-full text-[14px]">
              <thead className="bg-brand-paper-2 text-left text-[11.5px] uppercase tracking-wider text-brand-muted">
                <tr>
                  <th className="px-3 py-2">Partner</th>
                  <th className="px-3 py-2">Company</th>
                  <th className="px-3 py-2">Contact</th>
                  <th className="px-3 py-2">Submitted</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Owed</th>
                </tr>
              </thead>
              <tbody>
                {referrals.data.map((r) => (
                  <tr key={r.id} className="border-t border-brand-line">
                    <td className="px-3 py-2 text-brand-muted">{r.partner_email}</td>
                    <td className="px-3 py-2">{r.company_name}</td>
                    <td className="px-3 py-2 text-brand-muted">{r.contact_email}</td>
                    <td className="px-3 py-2 text-brand-muted">
                      {new Date(r.submitted_at).toLocaleDateString()}
                    </td>
                    <td className="px-3 py-2">{r.status}</td>
                    <td className="px-3 py-2 tabular-nums">{money(r.commission_cents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </main>
  );
}

function PartnerCard({
  row,
  onSave,
}: {
  row: PartnerRow;
  onSave: (patch: {
    status: "pending" | "approved" | "rejected" | "suspended";
    affiliateSlug?: string | null;
    commissionPct?: number | null;
    adminNote?: string | null;
  }) => Promise<void>;
}) {
  const [slug, setSlug] = React.useState(row.affiliate_slug ?? "");
  const [pct, setPct] = React.useState(String(row.commission_pct));
  const [note, setNote] = React.useState(row.admin_note ?? "");
  const [busy, setBusy] = React.useState(false);

  const save = async (status: "pending" | "approved" | "rejected" | "suspended") => {
    setBusy(true);
    try {
      await onSave({
        status,
        affiliateSlug: slug.trim() || null,
        commissionPct: parseInt(pct, 10) || null,
        adminNote: note.trim() || null,
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl border border-brand-line bg-brand-paper p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <div className="text-[15px] font-semibold text-brand-ink">
            {row.contact_name}
            {row.company_name ? `, ${row.company_name}` : ""}
          </div>
          <div className="text-[13px] text-brand-muted">
            {row.email} · {row.partner_type ?? "unspecified"} ·{" "}
            {new Date(row.applied_at).toLocaleDateString()}
          </div>
        </div>
        <span className="rounded-full bg-brand-paper-2 px-2 py-[2px] text-[11px] uppercase tracking-wider text-brand-muted">
          {row.status}
        </span>
      </div>
      {row.website && (
        <div className="mt-1 text-[13px] text-brand-muted">{row.website}</div>
      )}
      {row.audience && (
        <p className="mt-2 text-[13.5px] leading-relaxed text-brand-ink">{row.audience}</p>
      )}
      <div className="mt-3 grid gap-2 sm:grid-cols-3">
        <Input
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
          placeholder="link slug"
          maxLength={120}
        />
        <Input
          value={pct}
          onChange={(e) => setPct(e.target.value)}
          placeholder="commission %"
          type="number"
          min={1}
          max={100}
        />
        <Input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="internal note"
          maxLength={2000}
        />
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button disabled={busy} onClick={() => save("approved")}>
          Approve
        </Button>
        <Button variant="outline" disabled={busy} onClick={() => save("pending")}>
          Keep pending
        </Button>
        <Button variant="outline" disabled={busy} onClick={() => save("suspended")}>
          Suspend
        </Button>
        <Button variant="outline" disabled={busy} onClick={() => save("rejected")}>
          Reject
        </Button>
      </div>
    </div>
  );
}
